import { MutationTree } from "vuex";

import { IFlightBooking } from "@/core/interfaces/IFlightBooking.interface";
import {
  AdditionalPassengerField,
  BookingActions,
  BookingAlert,
  BookingPromoDealDetails,
  EditPassengerApiErrors,
  FlightDetailsType,
  IRepeatableFrequentFlyersRow,
} from "@/core/types/flight";
import {
  Traveler,
  UpdateTravelerType,
  IUpdatePassengerByIndex,
  AdditionalFields,
  IAdditionalSurcharges,
  CreatedBy,
} from "@/core/types/flight/FlightBookingForm";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import {
  AvailableAddOns,
  BaggageRequest,
  MealRequest,
  SeatRequest,
} from "../constants";
import { OrganizationUser } from "@/modules/FDG/models/fdgBookingDetail.response";
import type { BookingApprovalRequest } from "@/modules/Booking/types";

const syncFlightDetailsIdsFromAddOns = (
  flightDetails: FlightDetailsType | null | undefined,
  addOns: AvailableAddOns[],
) => {
  if (!flightDetails?.flight_legs?.length || !addOns.length) return;

  addOns.forEach((addonLeg, legIndex) => {
    const flightLeg = flightDetails.flight_legs[legIndex];
    const legDetails = addonLeg?.leg_details;
    if (!flightLeg || !legDetails) return;

    if (legDetails.public_id) {
      flightLeg.public_id = legDetails.public_id;
    }

    const addonSegments = legDetails.segments || [];
    addonSegments.forEach((addonSegment, segmentIndex) => {
      const flightSegment = flightLeg.segment?.[segmentIndex];
      const segmentPublicId = addonSegment?.segment_details?.public_id;
      if (flightSegment && segmentPublicId) {
        flightSegment.public_id = segmentPublicId;
      }
    });
  });
};

const mutations: MutationTree<IFlightBooking> = {
  resetExtras(state) {
    state.extraMeals = null;
    state.extraBaggages = null;
    state.extraSeats = null;
  },
  resetAddOns(state) {
    state.availableAddOns = null;
    state.extraMeals = null;
    state.extraBaggages = null;
    state.extraSeats = null;
  },
  addExtraMeals(state, payload: MealRequest) {
    if (!state.extraMeals) state.extraMeals = [];
    state.extraMeals = state.extraMeals.filter(
      (meal) =>
        meal.segment_public_id !== payload.segment_public_id ||
        meal.passenger_public_id !== payload.passenger_public_id,
    );
    state.extraMeals.push(payload);
  },
  addExtraSeats(state, payload: SeatRequest) {
    if (!state.extraSeats) state.extraSeats = [];
    state.extraSeats = state.extraSeats.filter(
      (seat) =>
        seat.segment_public_id !== payload.segment_public_id ||
        seat.passenger_public_id !== payload.passenger_public_id,
    );
    state.extraSeats.push(payload);
  },
  addExtraBaggages(state, payload: BaggageRequest) {
    if (!state.extraBaggages) {
      state.extraBaggages = [];
    }
    const index = state.extraBaggages.findIndex(
      (baggage) =>
        baggage.leg_public_id === payload.leg_public_id &&
        baggage.passenger_public_id === payload.passenger_public_id,
    );
    if (index !== -1) {
      state.extraBaggages.splice(index, 1);
    }
    state.extraBaggages.push(payload);
  },
  removeExtraMeal(state, payload: MealRequest) {
    if (!state.extraMeals) return;
    const index = state.extraMeals.findIndex(
      (meal) =>
        meal.meal_code === payload.meal_code &&
        meal.segment_public_id === payload.segment_public_id &&
        meal.passenger_public_id === payload.passenger_public_id,
    );
    if (index !== -1) {
      state.extraMeals.splice(index, 1);
    }
  },
  removeExtraSeat(state, payload: SeatRequest) {
    if (!state.extraSeats) return;
    const index = state.extraSeats.findIndex(
      (seat) =>
        seat.seat_number === payload.seat_number &&
        seat.segment_public_id === payload.segment_public_id &&
        seat.passenger_public_id === payload.passenger_public_id,
    );
    if (index !== -1) {
      state.extraSeats.splice(index, 1);
    }
  },
  removeExtraBaggage(state, payload: BaggageRequest) {
    if (!state.extraBaggages) return;
    const index = state.extraBaggages.findIndex(
      (baggage) =>
        baggage.baggage_code === payload.baggage_code &&
        baggage.leg_public_id === payload.leg_public_id &&
        baggage.passenger_public_id === payload.passenger_public_id,
    );
    if (index !== -1) {
      state.extraBaggages.splice(index, 1);
    }
  },
  setLoading(state, payload: boolean) {
    state.isLoading = payload;
  },
  setAddOnsLoading(state, payload: boolean) {
    state.isAddOnsLoading = payload;
  },
  setSkipAddOnsLoading(state, payload: boolean) {
    state.isSkipAddOnsLoading = payload;
  },
  saveAddOns(state, payload: AvailableAddOns[]) {
    const addOns = Array.isArray(payload) ? payload : [];
    state.availableAddOns = addOns;
    syncFlightDetailsIdsFromAddOns(state.flightDetails, addOns);
  },
  setConfirmBookingLoading(state, payload: boolean) {
    state.confirmBookingLoading = payload;
  },
  setBookingDetailError(state, payload) {
    state.errors = payload;
  },
  setDownloadTicketLoading(state, payload: boolean) {
    state.downloadTicketLoading = payload;
  },
  setCancelBookingLoading(state, payload: boolean) {
    state.cancelBookingLoading = payload;
  },
  saveBookingId(state, payload: string) {
    state.bookingId = payload;
  },
  saveFlightDetails(state, payload: FlightDetailsType) {
    state.flightDetails = payload;
    if (Array.isArray(state.availableAddOns) && state.availableAddOns.length) {
      syncFlightDetailsIdsFromAddOns(
        state.flightDetails,
        state.availableAddOns,
      );
    }
  },
  saveAdditionalPassengerFields(
    state,
    payload: AdditionalPassengerField[] | null,
  ) {
    state.additionalPassengerFields = payload;
  },
  saveBookingAlerts(state, payload: BookingAlert[] | null) {
    state.bookingAlerts = payload;
  },
  saveApprovalRequest(state, payload: BookingApprovalRequest | null) {
    state.approvalRequest = payload;
  },
  saveFrequentFlyersAllowed(state, payload: boolean) {
    state.frequentFlyersAllowed = payload;
  },
  saveOrganizationName(state, payload: string | null) {
    state.organizationName = payload;
  },
  savePlatformId(state, payload: string | null) {
    state.platformId = payload;
  },
  saveBookingStatus(state, payload: string) {
    state.bookingStatus = payload;
  },
  saveAddonsMeta(
    state,
    payload: { isAddonsEnabled: boolean; addonsStatus: string },
  ) {
    state.isAddonsEnabled = payload.isAddonsEnabled;
    state.addonsStatus = payload.addonsStatus;
  },
  saveBookingActions(state, payload: BookingActions) {
    state.bookingActions = payload;
  },

  saveBookingFinancialProfile(state, payload: string | null) {
    state.financialProfile = payload;
  },
  saveBookingPromoDealDetails(state, payload: BookingPromoDealDetails) {
    state.bookingPromoDealDetails = payload;
  },
  savePnrExpiry(state, payload: string) {
    state.pnrExpiryDate = payload;
  },
  saveBookingOrganizationUser(state, payload: OrganizationUser) {
    state.organizationUser = payload;
  },
  saveBookingCreatedBy(state, payload: CreatedBy) {
    state.createdBy = payload;
  },
  saveTravelers(state, payload: Array<Traveler>) {
    state.travelers = payload;
  },
  setEditingPassenger(state, payload: boolean) {
    state.isEditingPassenger = payload;
  },
  updateTaveler(state, payload: UpdateTravelerType) {
    const updatedTraveler: any = state.travelers[payload.index];
    updatedTraveler[payload.key] = payload.value;
    state.travelers[payload.index] = updatedTraveler;
  },
  updateTravelerFrequentFlyers(
    state,
    payload: { index: number; frequentFlyers: IRepeatableFrequentFlyersRow[] },
  ) {
    const traveler = state.travelers[payload.index];
    if (!traveler) return;
    traveler.frequent_flyers = payload.frequentFlyers;
  },
  updateTravelerAdditionalField(
    state,
    payload: { index: number; fieldCode: string; value: string },
  ) {
    const traveler = state.travelers[payload.index];

    if (!traveler.additional_data || Array.isArray(traveler.additional_data)) {
      traveler.additional_data = {};
    }

    (traveler.additional_data as AdditionalFields)[payload.fieldCode] =
      payload.value;
  },
  updateTravelerAdditionalFields(
    state,
    payload: { index: number; fields: AdditionalFields },
  ) {
    const traveler = state.travelers[payload.index];

    traveler.additional_data = {
      ...(traveler.additional_data || {}),
      ...payload.fields,
    };
  },
  resetTravelerAdditionalFields(state, index: number) {
    const traveler = { ...state.travelers[index] };

    if (traveler.additional_data && !Array.isArray(traveler.additional_data)) {
      const additionalData = traveler.additional_data as AdditionalFields;

      Object.keys(additionalData).forEach((key) => {
        additionalData[key] = "";
      });
    }

    state.travelers = [
      ...state.travelers.slice(0, index),
      traveler,
      ...state.travelers.slice(index + 1),
    ];
  },
  updateMobileNumber(state, payload: string) {
    state.mobileNumber = payload;
  },
  updateEmail(state, payload: string) {
    state.email = payload;
  },
  setVoidTicketLoading(state, payload: boolean) {
    state.voidTicketLoading = payload;
  },
  setPassengers(state, payload: IPassenger[]) {
    state.passengers = payload;
  },
  updatePassenger(state, updatedPassenger: IPassenger) {
    const passengers = state.flightDetails?.passengers;
    if (!passengers) return;

    const index = passengers.findIndex(
      (p) => p.public_id === updatedPassenger.public_id,
    );

    if (index !== -1) {
      passengers.splice(index, 1, updatedPassenger);
    }
  },
  setPassengerLoading(state, payload: boolean) {
    state.isPassengerLoading = payload;
  },
  setEditPassengerApiErrors(state, errors: EditPassengerApiErrors | null) {
    state.editPassengerApiErrors = errors;
  },

  clearPassengerByIndex(state, payload: IUpdatePassengerByIndex) {
    const traveler: any = { ...state.travelers[payload.index] };
    if ("public_id" in traveler) {
      delete traveler.public_id;
    }
    Object.keys(payload.value).forEach((key) => {
      const travelerKey = key as keyof Traveler;
      traveler[travelerKey] = payload.value[travelerKey];
    });

    state.travelers[payload.index] = traveler;
  },

  updatePassengerByIndex(state, payload: IUpdatePassengerByIndex) {
    const updatedTraveler: any = { ...state.travelers[payload.index] };

    Object.keys(payload.value).forEach((key) => {
      const travelerKey = key as keyof Traveler;
      updatedTraveler[travelerKey] = payload.value[travelerKey];
    });

    state.travelers[payload.index] = updatedTraveler;
  },
  setAdditionalSurcharges(state, payload: IAdditionalSurcharges) {
    state.AdditionalSurcharges = payload;
  },
  saveMarketingAirlineCodes(state: IFlightBooking, codes: string[]) {
    state.marketingAirlineCodes = codes;
  },
  saveBookingOrganizationId(state, payload: string | null) {
    state.organizationId = payload;
  },
};

export default mutations;
